// Módulo de Inventarios: consulta de ajustes de Sysplus, ajuste de precios y exportación a Excel.
//
// • "🧮 Recalcular precios" (todos los ajustes cargados) y "💰 Ajustar" (un solo ajuste) llaman al backend,
//   que consulta el costo promedio de cada artículo con la SP COSTO_PROMEDIO de Sysplus.
// • "📊 Exportar" (por fila) exporta SOLO un ajuste a un archivo Excel (2 hojas: Ajuste + Detalle).
// • Costo promedio 0 = ALERTA: no se modifica el costo y la línea se marca en rojo (modal) y en el Excel.
// • Regla en el Excel: solo se cambia el costo de líneas con cantidad ≠ 0 (existencia nueva − anterior)
//   y costo original 0. Para reemplazar todo costo con precio calculado, SOLO_SI_COSTO_CERO = false.
//
// El modal se renderiza con createPortal a document.body → nunca hereda el scroll de contenedores padre.
// Archivo 100% autocontenido: no requiere darkMode:'class' en tailwind.config ni CSS global.
import React, { useState, useMemo, useEffect, useCallback } from "react"; // Importa React y los hooks usados
import { TEMAS_GLASS, useTemaOscuro } from "../../../utils/tema";// Paleta glass y tema oscuro persistente
import { aISO, isoDia, fmt } from "../../../utils/format"; // Fechas, números y orden compartidos
import {
  API, POR_PAGINA, DEBOUNCE_MS, NEUTRO_CHIP, FILTROS_REVISION, RANGOS_RAPIDOS, ESTADOS, BTN_PRIMARY,
  txt, lineasDe, aFormatoFirebird, estilosTema,
} from "./utils/constantes"; // Constantes y helpers compartidos
import { pedirJson } from "./utils/api"; // fetch JSON con timeout
import { necesitaPrecio, tieneAlerta } from "./utils/reglasPrecio"; // Reglas de precio
import { exportarLibro, mensajeExcel, nombreHojaUnico } from "./utils/exportarExcel"; // Exportación a Excel
import useAjustesFiltrados from "./hooks/useAjustesFiltrados"; // Filtros, orden y paginación
import useVistos from "./hooks/useVistos"; // Ajustes vistos/revisados (localStorage)
import usePrecios from "./hooks/usePrecios"; // Cálculo de precios con lotes y progreso
import KpisInventario, { Stepper, SkeletonCards } from "./components/KpisInventario"; // KPIs, Stepper y skeleton
import TablaAjustes, { SkeletonTabla } from "./components/TablaAjustes"; // Tabla de ajustes y skeleton
import { Banner, BarraProgreso } from "./components/Banner"; // Banner de mensajes y barra de progreso
import ModalDetalle from "./components/ModalDetalleAjuste"; // Modal con el detalle del ajuste

/* ================================= Componente ================================= */

// Componente principal del módulo de Inventarios
export default function Invent({ onBackToMenu }) {
  const [ajustes, setAjustes] = useState([]); // Lista de ajustes cargados del backend
  const [detalle, setDetalle] = useState([]); // Todas las líneas de detalle cargadas
  const [loading, setLoading] = useState(true); // true mientras se cargan los datos
  const [mensaje, setMensaje] = useState(null); // Mensaje visible en el banner
  const [recarga, setRecarga] = useState(0); // Contador para forzar recarga manual
  const [seleccionado, setSeleccionado] = useState(null); // Ajuste abierto en el modal
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false); // Panel de filtros desplegado o no
  const [vistaModal, setVistaModal] = useState("tabla"); // Vista del modal: tabla o tarjetas
  const [filtroRevision, setFiltroRevision] = useState("todos"); // "todos" | "pendientes" | "revisados"
  const [temaOscuro, setTemaOscuro] = useTemaOscuro(); // Tema persistente en localStorage

  const tema = temaOscuro ? TEMAS_GLASS.oscuro : TEMAS_GLASS.claro; // Paleta activa según el tema
  const { btnGlass, inputGlass } = estilosTema(tema); // Clases de botón e input del tema

  const lista = useAjustesFiltrados(ajustes); // Hook de filtros, orden y paginación
  const { filtros, actualizar, filtrados, paginacion } = lista; // Extrae lo necesario del hook
  const { busqueda, estado, bodega, concepto, inicio, fin } = filtros; // Desestructura los filtros activos

  const vistos = useVistos(); // Hook de ajustes vistos/revisados
  const { estaVisto, esPermanente, marcarSesion, togglePermanente, limpiarSesion, limpiarPermanentes, totalPermanentes } = vistos; // Extrae las funciones de vistos

  // Al cambiar filtros/búsqueda/fechas se olvida lo "visto en sesión". ⚠️ NO poner `vistos` en las deps (loop):
  // `limpiarSesion` es estable (useCallback sin deps).
  useEffect(() => {
    limpiarSesion(); // Vacía los vistos de la sesión
  }, [busqueda, estado, bodega, concepto, inicio, fin, filtroRevision, limpiarSesion]);

  // Aplica el filtro de revisión sobre los ajustes ya filtrados
  const filtradosConRevision = useMemo(() => {
    if (filtroRevision === "pendientes") return filtrados.filter((a) => !estaVisto(a.AJUS_ID)); // Solo no vistos
    if (filtroRevision === "revisados") return filtrados.filter((a) => estaVisto(a.AJUS_ID)); // Solo vistos
    return filtrados; // Todos
  }, [filtrados, filtroRevision, estaVisto]);

  // Cuenta pendientes y revisados para mostrar en los chips
  const conteoRevision = useMemo(() => {
    const revisados = filtrados.filter((a) => estaVisto(a.AJUS_ID)).length; // Cantidad de revisados
    return { pendientes: filtrados.length - revisados, revisados }; // Pendientes = total − revisados
  }, [filtrados, estaVisto]);

  // Agrupa las líneas de detalle por id de ajuste para acceso rápido
  const detallePorAjuste = useMemo(() => {
    const mapa = new Map(); // Mapa id → líneas
    detalle.forEach((d) => { // Recorre cada línea
      const id = String(d.AJUS_ID); // Id como string
      if (!mapa.has(id)) mapa.set(id, []); // Crea el grupo si no existe
      mapa.get(id).push(d); // Agrega la línea a su grupo
    });
    return mapa; // Devuelve el mapa agrupado
  }, [detalle]);

  const px = usePrecios({ detalle, detallePorAjuste, setMensaje }); // Hook de cálculo de precios
  const { precios, reiniciar } = px; // Extrae precios y la función de reinicio

  /* ------------------------------ Carga y persistencia ------------------------------ */
  // Carga ajustes y detalle del backend para el rango de fechas
  const cargar = useCallback(
    async (signal) => {
      setLoading(true); // Activa el estado de carga
      setMensaje(null); // Limpia mensajes previos
      try {
        const params = new URLSearchParams(); // Parámetros de query string
        if (inicio) params.append("inicio", aFormatoFirebird(inicio)); // Agrega fecha inicial en formato Firebird
        if (fin) params.append("fin", aFormatoFirebird(fin)); // Agrega fecha final en formato Firebird
        const qs = params.toString() ? `?${params}` : ""; // Query string o vacío

        const [dataAj, dataDet] = await Promise.all([
          pedirJson(`${API}/ajustes${qs}`, { signal }), // Pide los ajustes
          pedirJson(`${API}/ajustes/detalle${qs}`, { signal }), // Pide el detalle en paralelo
        ]);
        setAjustes(Array.isArray(dataAj.data) ? dataAj.data : []); // Guarda ajustes (o vacío si no es array)
        setDetalle(Array.isArray(dataDet.data) ? dataDet.data : []); // Guarda detalle (o vacío si no es array)
        reiniciar(); // Borra precios calculados de datos anteriores
      } catch (err) {
        if (err.name === "AbortError") return; // Cancelación: no muestra error
        setMensaje({ type: "error", text: `No se pudieron cargar los ajustes: ${err.message}.` }); // Muestra el error
      } finally {
        if (!signal.aborted) setLoading(false); // Desactiva la carga solo si no fue cancelada
      }
    },
    [inicio, fin, reiniciar]
  );

  // Dispara la carga con debounce al cambiar fechas o al refrescar
  useEffect(() => {
    const controller = new AbortController(); // Controlador para cancelar la carga
    const t = setTimeout(() => cargar(controller.signal), DEBOUNCE_MS); // Espera el debounce antes de cargar
    return () => { // Limpieza si cambian las dependencias o se desmonta
      clearTimeout(t); // Cancela el debounce pendiente
      controller.abort(); // Cancela la petición en vuelo
    };
  }, [cargar, recarga]);

  // Los avisos informativos se cierran solos a los 8 s; los warnings y errores requieren
  // cierre explícito porque suelen indicar algo que el usuario debe revisar antes de continuar.
  useEffect(() => {
    if (!mensaje || mensaje.type === "error" || mensaje.type === "warning") return undefined; // No autocierra errores ni warnings
    const t = setTimeout(() => setMensaje(null), 8000); // Cierra el mensaje tras 8 segundos
    return () => clearTimeout(t); // Cancela el cierre si cambia el mensaje
  }, [mensaje]);

  const cerrarModal = useCallback(() => setSeleccionado(null), []); // Cierra el modal (referencia estable)

  // Abre el modal de un ajuste y lo marca como visto en la sesión
  const abrirAjuste = useCallback(
    (ajuste) => {
      setSeleccionado(ajuste); // Abre el modal con ese ajuste
      marcarSesion(ajuste.AJUS_ID); // Lo marca como visto
    },
    [marcarSesion]
  );

  /* ------------------------------ Datos derivados ------------------------------ */
  // Totales para las tarjetas KPI
  const kpis = useMemo(
    () => ({
      total: ajustes.length, // Total de ajustes
      activos: ajustes.filter(ESTADOS.activos.pasa).length, // Ajustes activos
      anulados: ajustes.filter(ESTADOS.anulados.pasa).length, // Ajustes anulados
    }),
    [ajustes]
  );

  // Serie de ajustes por día (últimos 14 días con datos) para el sparkline
  const sparkVolumen = useMemo(() => {
    const porDia = new Map(); // Mapa día → cantidad
    ajustes.forEach((a) => { // Recorre cada ajuste
      const d = isoDia(a.AJUS_FECHA); // Día ISO del ajuste
      if (d) porDia.set(d, (porDia.get(d) || 0) + 1); // Suma uno a ese día
    });
    return [...porDia.keys()].sort().slice(-14).map((d) => porDia.get(d)); // Últimos 14 días en orden cronológico
  }, [ajustes]);

  // Cantidad de ajustes con al menos una línea en alerta
  const ajustesConAlerta = useMemo(
    () => ajustes.filter((a) => tieneAlerta(lineasDe(detallePorAjuste, a), precios)).length,
    [ajustes, detallePorAjuste, precios]
  );

  const totalRevisados = useMemo(() => ajustes.filter((a) => estaVisto(a.AJUS_ID)).length, [ajustes, estaVisto]); // Total de ajustes vistos o revisados

  const hayPrecios = Object.keys(precios).length > 0; // true si ya se calcularon precios

  // Líneas filtradas que necesitan precio y aún no tienen cálculo
  const pendientesPrecio = useMemo(() => {
    let n = 0; // Contador de pendientes
    filtrados.forEach((a) => // Recorre ajustes filtrados
      lineasDe(detallePorAjuste, a).forEach((d) => { // Recorre sus líneas
        if (necesitaPrecio(d) && !precios[txt(d.ARTI_COD)]) n++; // Cuenta si necesita precio y no está calculado
      })
    );
    return n; // Devuelve el total pendiente
  }, [filtrados, detallePorAjuste, precios]);

  /* --------------------------------- Exportación --------------------------------- */
  // Exporta un solo ajuste a Excel (hoja Ajuste + hoja Detalle)
  const exportarAjuste = async (ajuste) => {
    try {
      const r = await exportarLibro({
        ajustes: [ajuste], detallePorAjuste, precios, // Solo este ajuste, con detalle y precios
        archivo: `Ajuste_${ajuste.AJUS_ID}_${aISO(new Date())}.xlsx`, // Nombre del archivo con id y fecha
        hojaResumen: "Ajuste", // Nombre de la hoja resumen
        // Un solo ajuste → hoja fija "Detalle": no hay nada que deduplicar, por eso se ignoran (wb, id).
        nombreHoja: () => "Detalle",
      });
      setMensaje(mensajeExcel(`Ajuste #${ajuste.AJUS_ID} exportado con ${fmt(r.lineas, 3)} línea(s) de detalle.`, r)); // Informa el resultado
    } catch (err) {
      setMensaje({ type: "error", text: `No se pudo generar el Excel del ajuste #${ajuste.AJUS_ID}: ${err.message}.` }); // Informa el error
    }
  };

  // Exporta todos los ajustes visibles con los filtros actuales
  const exportarTodos = async () => {
    if (filtradosConRevision.length === 0) return setMensaje({ type: "warning", text: "No hay ajustes para exportar con los filtros actuales." }); // Nada que exportar

    if (pendientesPrecio > 0) { // Hay líneas sin recalcular
      // Texto de confirmación según se hayan calculado precios o no
      const texto = hayPrecios
        ? `Hay ${fmt(pendientesPrecio, 3)} líneas con costo 0 de artículos que aún no se han recalculado. ¿Deseas exportar igual?`
        : "No has recalculado los precios. El Excel puede tener productos con costo 0. ¿Deseas exportar igual?";
      if (!window.confirm(texto)) { // Si el usuario cancela
        return setMensaje({ type: "warning", text: "Exportación cancelada. Usa «Recalcular precios» y vuelve a exportar." }); // Informa la cancelación
      }
    }

    try {
      const r = await exportarLibro({
        ajustes: filtradosConRevision, detallePorAjuste, precios, // Todos los ajustes filtrados
        archivo: `Ajustes_${aISO(new Date())}.xlsx`, // Nombre del archivo con fecha
        hojaResumen: "Ajustes", // Nombre de la hoja resumen
        nombreHoja: nombreHojaUnico, // Una hoja de detalle por ajuste con nombre único
      });
      setMensaje(
        mensajeExcel(`Se exportaron ${fmt(filtradosConRevision.length, 3)} ajustes con ${fmt(r.lineas, 3)} líneas de detalle en total.`, r) // Informa el resultado
      );
    } catch (err) {
      setMensaje({ type: "error", text: `No se pudo generar el Excel: ${err.message}.` }); // Informa el error
    }
  };

  // Aplica un rango rápido de fechas a los filtros
  const aplicarRangoRapido = (rango) => {
    const [ini, fn] = rango.calc(); // Calcula inicio y fin del rango
    actualizar({ inicio: ini ? aISO(ini) : "", fin: fn ? aISO(fn) : "" }); // Actualiza fechas (vacío si es null)
  };

  // Configuración de las 5 tarjetas KPI
  const tarjetasKpi = [
    { label: "Total ajustes", valor: kpis.total, color: tema.textoFuerte, sparkColor: "#007aff", icono: "📋" }, // Total
    { label: "Activos", valor: kpis.activos, color: "text-emerald-700", sparkColor: "#10b981", icono: "🟢" }, // Activos
    { label: "Anulados", valor: kpis.anulados, color: "text-red-700", sparkColor: "#ef4444", icono: "🔴" }, // Anulados
    {
      label: "Con alertas", // Ajustes con alerta
      valor: ajustesConAlerta, // Cantidad con alerta
      color: ajustesConAlerta > 0 ? "text-red-700" : tema.textoSuave, // Rojo solo si hay alertas
      sparkColor: "#dc2626", // Color del sparkline
      icono: "⚠️", // Icono de la tarjeta
    },
    { label: "Revisados", valor: totalRevisados, color: "text-emerald-700", sparkColor: "#10b981", icono: "✅" }, // Revisados
  ];

  const hayError = mensaje?.type === "error"; // true si el mensaje actual es un error
  const { setPagina } = paginacion; // Setter de página del hook
  // Paginación sobre la lista ya filtrada por revisión (el hook pagina `filtrados`, que es un superconjunto).
  const totalPaginas = Math.max(1, Math.ceil(filtradosConRevision.length / POR_PAGINA)); // Total de páginas (mínimo 1)
  const paginaActual = Math.min(paginacion.paginaActual, totalPaginas); // Página actual limitada al total
  const desde = (paginaActual - 1) * POR_PAGINA; // Índice inicial de la página
  const visiblesRevision = filtradosConRevision.slice(desde, desde + POR_PAGINA); // Filas de la página actual

  useEffect(() => {
    setPagina(1); // Vuelve a la primera página al cambiar el filtro de revisión
  }, [filtroRevision, setPagina]);

  /* ----------------------------------- Vista ----------------------------------- */
  return (
    // Contenedor raíz del módulo; se oculta a lectores de pantalla cuando el modal está abierto
    <div
      data-invent-root
      aria-hidden={seleccionado ? "true" : undefined}
      className={`${tema.fondo} ${tema.borde} backdrop-blur-[35px] rounded-[34px] shadow-[0_25px_70px_rgba(0,0,0,0.12)] p-6 sm:p-10 max-w-7xl w-full mx-auto max-h-[92vh] overflow-y-auto border relative`}
      style={{ fontFamily: "inherit" }}
    >
      {/* Bloque de CSS con las animaciones usadas por el módulo (fade, scale, pulse y slide) */}
      <style>{`
        @keyframes inv-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes inv-scale-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
        @keyframes inv-pulse-slow { 0%, 100% { opacity: 1; } 50% { opacity: 0.85; } }
        @keyframes inv-slide-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .inv-fade-in { animation: inv-fade-in 0.2s ease-out; }
        .inv-scale-in { animation: inv-scale-in 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
        .inv-pulse-slow { animation: inv-pulse-slow 2.5s ease-in-out infinite; }
        .inv-slide-up { animation: inv-slide-up 0.3s ease-out; }
      `}</style>

      {/* Header */}
      <div className={`flex flex-col md:flex-row justify-between items-start md:items-center border-b ${tema.bordeInterno} pb-6 mb-6 gap-4`}>
        {/* Lado izquierdo: icono y títulos */}
        <div className="flex items-center gap-3.5">
          {/* Icono del módulo */}
          <div className={`w-12 h-12 rounded-2xl ${tema.inputBg} border ${tema.btnBorde} flex items-center justify-center text-2xl shadow-sm`}>
            📦
          </div>
          {/* Título y subtítulo */}
          <div>
            <h2 className={`text-2xl font-extrabold tracking-tight ${tema.textoFuerte}`}>Módulo de Inventarios</h2>
            <p className={`text-sm font-medium mt-0.5 ${tema.textoSuave}`}>Consulta de ajustes desde Sysplus.</p>
          </div>
        </div>
        {/* Lado derecho: botones de tema, refrescar y volver */}
        <div className="flex items-center gap-2">
          {/* Alterna entre tema claro y oscuro */}
          <button
            onClick={() => setTemaOscuro((v) => !v)}
            className={btnGlass}
            title={temaOscuro ? "Modo claro" : "Modo oscuro"}
            aria-label="Cambiar tema"
          >
            {temaOscuro ? "☀️" : "🌙"}
          </button>
          {/* Fuerza una recarga de datos */}
          <button onClick={() => setRecarga((n) => n + 1)} disabled={loading} className={btnGlass}>
            🔄 Refrescar
          </button>
          {/* Vuelve al menú principal */}
          <button onClick={onBackToMenu} className={btnGlass}>
            Volver al Menú
          </button>
        </div>
      </div>

      {/* Stepper de flujo, visible cuando no está cargando */}
      {!loading && <Stepper pendientes={pendientesPrecio} hayPrecios={hayPrecios} totalAjustes={ajustes.length} tema={tema} />}

      {/* Banner de mensajes; Reintentar usa la acción del mensaje o recarga */}
      {mensaje && (
        <Banner
          mensaje={mensaje}
          onReintentar={() => (mensaje.reintentar ? mensaje.reintentar() : setRecarga((n) => n + 1))}
          onCerrar={() => setMensaje(null)}
        />
      )}

      {/* Barra de progreso mientras se calculan precios */}
      {px.progreso && <BarraProgreso progreso={px.progreso} />}

      {/* Cargando: skeletons; error sin datos: nada; si no, contenido completo */}
      {loading ? (
        <>
          {/* Placeholders de tarjetas y tabla */}
          <SkeletonCards tema={tema} />
          <SkeletonTabla tema={tema} />
        </>
      ) : hayError && ajustes.length === 0 ? null : (
        <>
          {/* Tarjetas KPI */}
          <KpisInventario tarjetasKpi={tarjetasKpi} sparkVolumen={sparkVolumen} tema={tema} />

          {/* Botones de rangos rápidos de fecha */}
          <div className="flex flex-wrap gap-2 mb-3">
            {RANGOS_RAPIDOS.map((rango) => (
              <button
                key={rango.id}
                onClick={() => aplicarRangoRapido(rango)}
                className={`px-4 py-2 rounded-full ${tema.btnBg} ${tema.btnTexto} text-[11px] font-bold border ${tema.btnBorde} shadow-sm cursor-pointer transition-colors`}
              >
                {rango.label}
              </button>
            ))}
          </div>

          {/* Chips de filtro por revisión y botón de reset de permanentes */}
          <div className="flex flex-wrap items-center gap-2 mb-3" role="group" aria-label="Filtrar por revisión">
            {FILTROS_REVISION.map((f) => (
              <button
                key={f.id}
                onClick={() => setFiltroRevision(f.id)}
                aria-pressed={filtroRevision === f.id}
                className={`px-4 py-2 rounded-full text-[11px] font-bold border shadow-sm cursor-pointer transition-colors ${
                  filtroRevision === f.id ? f.activo : NEUTRO_CHIP
                }`}
              >
                {f.label}
                {f.id !== "todos" ? ` (${fmt(conteoRevision[f.id], 3)})` : ""}
              </button>
            ))}
            {/* Botón para quitar todas las marcas permanentes, solo si existen */}
            {totalPermanentes > 0 && (
              <button
                onClick={limpiarPermanentes}
                className="px-4 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-800 text-[11px] font-bold border border-red-400/40 shadow-sm cursor-pointer transition-colors"
              >
                🔄 Resetear permanentes ({fmt(totalPermanentes, 3)})
              </button>
            )}
          </div>

          {/* Tarjeta de búsqueda y filtros */}
          <div className={`${tema.cardBg} ${tema.cardBorde} backdrop-blur-md rounded-3xl border shadow-sm p-4 mb-6`}>
            {/* Fila: búsqueda, botón de filtros y botón limpiar */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
              {/* Campo de búsqueda de texto */}
              <input
                type="text"
                value={busqueda}
                onChange={(e) => actualizar({ busqueda: e.target.value })}
                placeholder="🔍 Buscar por ID, prefijo, bodega, número, concepto o usuario"
                aria-label="Buscar ajustes"
                className={`${inputGlass} lg:flex-1`}
              />
              {/* Despliega o pliega el panel de filtros, con contador de chips */}
              <button onClick={() => setFiltrosAbiertos((v) => !v)} className={`${btnGlass} whitespace-nowrap`} aria-expanded={filtrosAbiertos}>
                ⚙️ Filtros {lista.chips.length > 0 ? `(${lista.chips.length})` : ""} {filtrosAbiertos ? "▲" : "▼"}
              </button>
              {/* Limpia filtros y filtro de revisión; deshabilitado si no hay nada que limpiar */}
              <button
                onClick={() => {
                  lista.limpiar(); // Limpia búsqueda, estado, bodega y concepto
                  setFiltroRevision("todos"); // Restablece el filtro de revisión
                }}
                disabled={!lista.hayFiltros && filtroRevision === "todos"}
                className={`${btnGlass} whitespace-nowrap`}
                title="Limpiar filtros"
              >
                🧹 Limpiar
              </button>
            </div>

            {/* Panel desplegable con selectores de bodega, concepto, estado y fechas */}
            {filtrosAbiertos && (
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-3 pt-3 border-t ${tema.bordeSuave} inv-slide-up`}>
                {/* Selector de bodega */}
                <select value={bodega} onChange={(e) => actualizar({ bodega: e.target.value })} aria-label="Filtrar por bodega" className={inputGlass}>
                  <option value="">Todas las bodegas</option>
                  {lista.bodegas.map((b) => (
                    <option key={b} value={b}>
                      Bodega {b}
                    </option>
                  ))}
                </select>
                {/* Selector de concepto */}
                <select value={concepto} onChange={(e) => actualizar({ concepto: e.target.value })} aria-label="Filtrar por concepto" className={inputGlass}>
                  <option value="">Todos los conceptos</option>
                  {lista.conceptos.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {/* Selector de estado */}
                <select value={estado} onChange={(e) => actualizar({ estado: e.target.value })} aria-label="Filtrar por estado" className={inputGlass}>
                  {Object.entries(ESTADOS).map(([valor, { label }]) => (
                    <option key={valor} value={valor}>
                      {label}
                    </option>
                  ))}
                </select>
                {/* Fecha de inicio */}
                <input type="date" value={inicio} onChange={(e) => actualizar({ inicio: e.target.value })} aria-label="Fecha de inicio" className={inputGlass} />
                {/* Fecha de fin */}
                <input type="date" value={fin} onChange={(e) => actualizar({ fin: e.target.value })} aria-label="Fecha de fin" className={inputGlass} />
              </div>
            )}

            {/* Chips de filtros activos con botón para quitar cada uno */}
            {lista.chips.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {lista.chips.map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 text-blue-800 text-[11px] font-bold border border-blue-500/20"
                  >
                    {chip.label}
                    <button onClick={chip.onRemove} className="hover:text-red-600 cursor-pointer" aria-label={`Quitar filtro ${chip.label}`}>
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Botones de acción global: recalcular precios y exportar Excel */}
          <div className="flex flex-col lg:flex-row gap-3 mb-6 lg:items-center">
            {/* Recalcula precios de los ajustes filtrados */}
            <button
              onClick={() => px.recalcular(filtrados)}
              disabled={px.calculando || loading}
              className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-all shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed lg:ml-auto whitespace-nowrap"
            >
              {px.calculando ? "⏳ Calculando..." : "🧮 Recalcular precios"}
            </button>
            {/* Exporta a Excel; resalta si hay precios pendientes */}
            <button onClick={exportarTodos} className={`${BTN_PRIMARY} ${pendientesPrecio > 0 ? "ring-2 ring-amber-400/50" : ""}`}>
              📊 Exportar Excel
            </button>
          </div>

          {/* Sin resultados: estado vacío; con resultados: tabla y paginación */}
          {filtradosConRevision.length === 0 ? (
            <div className={`py-16 flex flex-col items-center gap-2 ${tema.textoSuave}`}>
              <div className="text-5xl">📂</div>
              <p className="text-sm font-bold">No hay ajustes para mostrar</p>
              <p className="text-xs font-medium">Cambia los filtros o el rango de fechas para ver resultados.</p>
            </div>
          ) : (
            <>
              {/* Tabla de ajustes de la página actual */}
              <TablaAjustes
                tema={tema}
                lista={lista}
                visiblesRevision={visiblesRevision}
                detallePorAjuste={detallePorAjuste}
                precios={precios}
                px={px}
                loading={loading}
                estaVisto={estaVisto}
                esPermanente={esPermanente}
                abrirAjuste={abrirAjuste}
                exportarAjuste={exportarAjuste}
              />

              {/* Pie con texto de rango y botones de paginación */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4">
                {/* Texto "Mostrando X - Y de Z ajustes" */}
                <p className={`text-xs font-medium ${tema.textoSuave}`}>
                  Mostrando {fmt(desde + 1, 3)} - {fmt(Math.min(desde + POR_PAGINA, filtradosConRevision.length), 3)} de {fmt(filtradosConRevision.length, 3)} ajustes
                </p>
                {/* Controles Anterior / página / Siguiente */}
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagina(paginaActual - 1)} disabled={paginaActual <= 1} className={btnGlass}>
                    Anterior
                  </button>
                  <span className={`text-xs font-bold ${tema.textoNormal}`}>
                    {paginaActual} / {totalPaginas}
                  </span>
                  <button onClick={() => setPagina(paginaActual + 1)} disabled={paginaActual >= totalPaginas} className={btnGlass}>
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal de detalle, solo cuando hay un ajuste seleccionado */}
      {seleccionado && (
        <ModalDetalle
          ajuste={seleccionado}
          lineas={lineasDe(detallePorAjuste, seleccionado)}
          precios={precios}
          tema={tema}
          vista={vistaModal}
          setVista={setVistaModal}
          onClose={cerrarModal}
          onExportar={exportarAjuste}
          visto={estaVisto(seleccionado.AJUS_ID)}
          esPermanente={esPermanente(seleccionado.AJUS_ID)}
          onTogglePermanente={() => togglePermanente(seleccionado.AJUS_ID)}
        />
      )}
    </div>
  );
}